import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { api } from '../api.js';
import { useSession } from '../auth-client.js';
import CoverImageUploader from '../components/CoverImageUploader.js';
import QuestionTypeBlockEditor from '../components/QuestionTypeBlockEditor.js';
import {
  isQuestionBlockStarted,
  isQuestionBlockValid,
  newBabyShowerBlockDraft,
  newBirthDateBlockDraft,
  newMommyDaddyBlockDraft,
  newQuestionBlockDraft,
  toQuestionDraft,
  type QuestionBlockDraft,
} from '../lib/customQuestionDraft.js';

/**
 * Adding a custom-competition bolão to an existing circle (PLAN.md Phase
 * 10.3/10.4, 10.18) — the football path's `CreateGroupScreen` with
 * "Competition" swapped for "Question type", since there is no
 * pre-existing competition to pick: the organizer is authoring the
 * questions themselves, and can leave all of them for later (this screen
 * submits fine with zero finished blocks).
 *
 * Mounted at `/groups/:id/bolaos/new/custom` — the circle already exists by
 * the time this screen is reached; creating one is a separate, earlier step
 * (`CreateCircleScreen`).
 *
 * `preset` (PLAN.md Phase 10.11/10.13) is what the Baby Shower tile on the
 * sport picker leads to: the very same screen, opened with its name and its
 * questions already filled in. Deliberately a prefill rather than a separate
 * flow — a preset that could produce something the plain custom editor
 * can't would be a second, divergent code path to keep working, and the
 * organizer can still edit or delete every part of what it filled in before
 * pressing Create.
 */

/**
 * The MOMMY-or-DADDY prompts the Baby Shower template ships with, in the
 * order they appear (PLAN.md Phase 10.13). Keys rather than literals so both
 * locales seed the organizer's starting text in their own language — it
 * becomes their own stored words the moment they press Create.
 */
const MOMMY_DADDY_QUESTION_KEYS = [
  'disciplinarian',
  'diapers',
  'newbornWeight',
  'nervousAboutLabor',
  'doctorQuestions',
  'pickyEater',
  'moreKids',
  'morePhotos',
  'funParent',
  'patientParent',
  'upAtNight',
  'suggestedName',
  'spoilTheBaby',
  'talksToTheBump',
  'funOutfits',
] as const;

/**
 * How many blank question blocks the template leaves at the end, for the
 * organizer's own questions. They submit as nothing — an untouched block is
 * neither "started" nor valid, so it is simply dropped — which is what makes
 * leaving them safe rather than something to clean up first.
 */
const BABY_SHOWER_BLANK_BLOCKS = 2;

/** The whole Baby Shower template: the gender question, the birth-date guess, the MOMMY/DADDY set, then room for more. */
function babyShowerBlocks(t: (key: string) => string): QuestionBlockDraft[] {
  return [
    newBabyShowerBlockDraft({
      prompt: t('customCompetition.babyShower.prompt'),
      boy: t('customCompetition.babyShower.boy'),
      girl: t('customCompetition.babyShower.girl'),
    }),
    // Second, right after the gender reveal: the other question everyone at a
    // baby shower already has an opinion about, and the one whose answer takes
    // longest to arrive.
    newBirthDateBlockDraft({ prompt: t('customCompetition.babyShower.birthDatePrompt') }),
    ...MOMMY_DADDY_QUESTION_KEYS.map((key) =>
      newMommyDaddyBlockDraft({
        prompt: t(`customCompetition.babyShower.questions.${key}`),
        mommy: t('customCompetition.babyShower.mommy'),
        daddy: t('customCompetition.babyShower.daddy'),
      }),
    ),
    ...Array.from({ length: BABY_SHOWER_BLANK_BLOCKS }, () => newQuestionBlockDraft()),
  ];
}

/** The custom-competition creation screen itself — see the module doc above. */
export default function CreateCustomCompetitionScreen({ preset }: { preset?: 'baby-shower' } = {}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const queryClient = useQueryClient();

  const isBabyShower = preset === 'baby-shower';
  const [name, setName] = useState(
    isBabyShower ? t('customCompetition.babyShower.defaultName') : '',
  );
  const [joinAs, setJoinAs] = useState('');
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [blocks, setBlocks] = useState<QuestionBlockDraft[]>(() =>
    isBabyShower ? babyShowerBlocks(t) : [newQuestionBlockDraft()],
  );
  const { data: session } = useSession();

  // Defaults "play as" to the organizer's own account name, same as
  // `CreateGroupScreen` — deliberately set directly rather than through the
  // field's own `onChange`, so this prefill never flips `hasEdited` below
  // and freezes the baby-shower resync on its own.
  useEffect(() => {
    if (session?.user?.name && !joinAs) setJoinAs(session.user.name);
  }, [session?.user?.name, joinAs]);

  // Whether the organizer has touched anything since this screen mounted —
  // the prefilled name and question blocks are only ever synced to the
  // account's language while this stays false (below). The name field is
  // watched here too, even though it isn't language-sensitive on its own:
  // once the organizer has started customizing the bolão, nothing about the
  // preset should be silently rewritten under them.
  const [hasEdited, setHasEdited] = useState(false);

  // The i18next language resolution that decides what this preset is written
  // in doesn't finish before this screen can mount: `RequireAccount`
  // (`App.tsx`) applies the signed-in account's *saved* language preference
  // in its own effect, once the session loads, which can land after this
  // screen's initial `useState` above has already captured `t` in whatever
  // language `localStorage`/the browser guessed first. Without this, an
  // organizer whose account is set to Portuguese but who opens the app on a
  // browser that still resolves English first gets an otherwise-Portuguese
  // UI with an English "Predict Baby's Gender" question baked in — the exact
  // bug this resyncs. Gated on `hasEdited` so a language switch mid-edit
  // (the Settings toggle) never overwrites real customization.
  useEffect(() => {
    if (!isBabyShower || hasEdited) return;
    setName(t('customCompetition.babyShower.defaultName'));
    setBlocks(babyShowerBlocks(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resync only on an actual language change, not every render `t` is recreated.
  }, [i18n.resolvedLanguage]);

  const createPool = useMutation({
    mutationFn: async (questions: ReturnType<typeof toQuestionDraft>[]) => {
      const pool = await api.createCustomPool(id, {
        name: name.trim(),
        questions,
        ...(imageUrl ? { imageUrl } : {}),
      });
      if (joinAs.trim()) await api.joinPool(id, pool.id, joinAs.trim());
      return pool;
    },
    onSuccess: async (pool) => {
      await queryClient.invalidateQueries({ queryKey: ['pools', id] });
      void navigate(`/groups/${id}/bolaos/${pool.id}`);
    },
  });

  const validBlocks = blocks.filter(isQuestionBlockValid);
  // A block the person started typing into but didn't finish shouldn't be
  // silently discarded on submit — better to make them either finish it or
  // explicitly remove it than lose what they typed with no explanation.
  const hasUnfinishedBlock = blocks.some(
    (b) => isQuestionBlockStarted(b) && !isQuestionBlockValid(b),
  );
  const canSubmit = name.trim().length > 0 && !hasUnfinishedBlock && !createPool.isPending;

  return (
    <main className="screen">
      <Link to={`/groups/${id}/bolaos/new`} className="muted">
        {t('common.back')}
      </Link>
      <h1>
        {isBabyShower ? t('customCompetition.babyShower.title') : t('createCompetition.title')}
      </h1>
      {isBabyShower ? <p className="muted">{t('customCompetition.babyShower.intro')}</p> : null}

      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          if (!canSubmit) return;
          createPool.mutate(validBlocks.map(toQuestionDraft));
        }}
      >
        <div className="panel stack" style={{ padding: 'var(--space-4)' }}>
          <div>
            <label className="label" htmlFor="name">
              {t('createCompetition.nameLabel')}
            </label>
            <input
              id="name"
              className="input"
              value={name}
              maxLength={80}
              placeholder={t('createCompetition.namePlaceholder')}
              onChange={(event) => {
                setHasEdited(true);
                setName(event.target.value);
              }}
            />
          </div>

          <div>
            <label className="label" htmlFor="joinAs">
              {t('createCompetition.joinLabel')}
            </label>
            <input
              id="joinAs"
              className="input"
              value={joinAs}
              maxLength={60}
              placeholder={t('createCompetition.joinPlaceholder')}
              onChange={(event) => {
                setHasEdited(true);
                setJoinAs(event.target.value);
              }}
            />
            <p className="muted" style={{ fontSize: '0.8rem', marginTop: 'var(--space-1)' }}>
              {t('createCompetition.joinHint')}
            </p>
          </div>

          <div>
            <span className="label">{t('createCompetition.imageLabel')}</span>
            <CoverImageUploader
              imageUrl={imageUrl}
              onChange={setImageUrl}
              disabled={createPool.isPending}
            />
          </div>
        </div>

        <div className="stack">
          {blocks.map((block, i) => (
            <QuestionTypeBlockEditor
              key={block.key}
              block={block}
              index={i + 1}
              onChange={(next) => {
                setHasEdited(true);
                setBlocks((bs) => bs.map((b) => (b.key === block.key ? next : b)));
              }}
              {...(blocks.length > 1
                ? {
                    onRemove: () => {
                      setHasEdited(true);
                      setBlocks((bs) => bs.filter((b) => b.key !== block.key));
                    },
                  }
                : {})}
            />
          ))}
        </div>

        <button
          type="button"
          className="button button-secondary"
          onClick={() => {
            setHasEdited(true);
            setBlocks((bs) => [...bs, newQuestionBlockDraft()]);
          }}
        >
          {t('customCompetition.addQuestionType')}
        </button>

        {createPool.isError ? <p className="error">{t('common.somethingWentWrong')}</p> : null}

        <button type="submit" className="button button-primary" disabled={!canSubmit}>
          {createPool.isPending ? t('createCompetition.creating') : t('createCompetition.submit')}
        </button>
      </form>
    </main>
  );
}
